import { ref, computed, watch, type Ref } from "vue";
import { DeliveryRequest } from "@/common/constant";
import {
  localStorageGetPageHeaders,
  localStorageSetPageHeaders,
} from "@/common/localStorageUtil";
import { displayPagingRangeFunction } from "@/common/commonFunction";
import type { TableHeader, ListData, ListOptions } from "../types";
import { DELIVERY_MOVE_LIST_FILE_NAME } from "../constants";

/**
 * 一覧テーブル表示に必要なデータ整形と列情報を提供する composable。
 *
 * @returns テーブル描画に利用する列定義と表示用データ
 */
export const useDeliveryMoveTable = () => {
  // 在庫移動・回収依頼一覧 ヘッダー デフォルト値
  const defaultTableHeaders: TableHeader[] = [
    {
      id: 1,
      display: true,
      title: "受付番号",
      settingText: "受付番号",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      value: "receptionNo",
    },
    {
      id: 15,
      display: false,
      title: "チェーン名",
      settingText: "チェーン名",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      value: "chainName",
    },
    {
      id: 2,
      display: true,
      title: "配達元",
      settingText: "配達元",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      value: "shipFromName",
    },
    {
      id: 3,
      display: true,
      title: "配達先",
      settingText: "配達先",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      value: "deliveryName",
    },
    {
      id: 4,
      display: true,
      title: "便名(配達時間帯)",
      settingText: "便名(配達時間帯)",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      value: "deliveryIdName",
    },
    {
      id: 5,
      display: false,
      title: "受付日時",
      settingText: "受付日時",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      value: "createdDate",
    },
    {
      id: 6,
      display: true,
      title: "配達日",
      settingText: "配達日",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      value: "plannedDeliveryDate",
    },
    {
      id: 7,
      display: true,
      title: "ステータス",
      settingText: "ステータス",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      value: "status",
    },
    {
      id: 8,
      display: true,
      title: "担当者",
      settingText: "担当者",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      value: "staffName",
    },
    // DSS側の配達予定時刻との連携を実装したらコメントアウトを解除する
    // {
    //   id: 9,
    //   display: true,
    //   text: "配達予定時刻",
    //   settingText: "配達予定時刻",
    //   align: "center",
    //   sortable: false,
    //   orderDisabled: true,
    //   order: "",
    //   orderKey: "",
    //   value: "deliveryEstimatedTime",
    // },
    {
      id: 10,
      display: true,
      title: "配達完了時刻",
      settingText: "配達完了時刻",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      value: "deliveryCompletedTime",
    },
    {
      id: 11,
      display: true,
      title: "配達タイプ",
      settingText: "配達タイプ",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      value: "deliveryType",
    },
    {
      id: 12,
      display: false,
      title: "支払方法",
      settingText: "支払方法",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      value: "paymentMethod",
    },
    {
      id: 13,
      display: false,
      title: "支払金額",
      settingText: "支払金額",
      align: "center",
      sortable: false,
      orderDisabled: true, // 並び替え機能を実装したら false にする
      order: "", // 並び替え機能を実装したら "asc" にする
      orderKey: "", // 並び替え機能を実装したら設定する
      value: "paymentAmount",
    },
    {
      id: 14,
      display: true,
      title: "",
      settingText: "(操作)",
      align: "center",
      sortable: false,
      orderDisabled: true,
      order: "",
      orderKey: "",
      value: "actions",
    },
  ];

  // 在庫移動・回収依頼一覧 ヘッダー
  const tableHeaders = ref<TableHeader[]>([]);

  // 在庫移動・回収依頼一覧 ヘッダー初期化
  const initTableHeaders = (): void => {
    const tableHeadersStr = localStorageGetPageHeaders(DELIVERY_MOVE_LIST_FILE_NAME + "_20230612");
    if (tableHeadersStr === null) {
      // localStorage に無ければ、デフォルト値を設定
      tableHeaders.value = JSON.parse(JSON.stringify(defaultTableHeaders));
    } else {
      // localStorage にあれば、それを設定
      tableHeaders.value = JSON.parse(tableHeadersStr);
    }
  };

  // 表示するものだけにした 在庫移動・回収依頼一覧 ヘッダー
  const filteredTableHeaders = computed((): TableHeader[] =>
    tableHeaders.value.filter((h) => h.display)
  );

  // 配達ステータス名
  const deliveryStatusName = DeliveryRequest.status;

  // ステータスID→名称変換
  const pickupDeliveryStatusName = (id: string | null | undefined): string => {
    const item = deliveryStatusName.find((s) => s.id === id);
    return item ? item.name : "";
  };

  // 配達タイプ名一覧
  const deliveryTypeItems = ref(DeliveryRequest.move_type);
  const pickupDeliveryTypeName = (value: number | null | undefined): string => {
    const item = deliveryTypeItems.value.find((t) => t.value === value);
    return item ? item.label : "";
  };

  // ローディング・オプション
  const listLoading = ref(false);
  const listOptions = ref<ListOptions>({
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    sortDesc: [],
    groupBy: [],
    groupDesc: [],
    multiSort: false,
    mustSort: false,
  });

  // 配達一覧データ
  const listDataArray = ref<ListData[]>([]);

  // 配達依頼一覧 絞込み後の全データ数
  const listDataTotalCount = ref(0);

  // 配達依頼一覧 ページ数
  const pageCount = computed(() =>
    Math.max(1, Math.ceil(listDataTotalCount.value / (listOptions.value.itemsPerPage || 1)))
  );

  // 選択されたチェックボックス
  const selectListCheckbox = ref<ListData[]>([]);

  // 選択されたチェックボックス(キャンセル可のみ)
  const filteredSelectListCheckbox = computed((): ListData[] =>
    selectListCheckbox.value.filter((v) => v.cancelAllowed)
  );

  // 検索ボタンからの呼び出し時にwatchをスキップするフラグ
  const isSearchButtonClick = ref(false);

  // ページング範囲の表示
  const displayPagingRange = ref<string>("");

  /**
   * 在庫移動・回収依頼一覧 ヘッダー設定用
   */
  const tableHeadersSetting = ref<TableHeader[]>([]);

  /**
   * 設定 @Click
   */
  const onClickListSetting = (): void => {
    // 在庫移動・回収依頼一覧 ヘッダー設定用 に現在の状態をコピー
    tableHeadersSetting.value = JSON.parse(
      JSON.stringify(tableHeaders.value)
    ) as TableHeader[];

    // 一覧設定ダイアログ表示
    displayListSettingDialog.value = true;
  };

  // 一覧設定ダイアログ表示フラグ（外部からも参照される）
  const displayListSettingDialog = ref<boolean>(false);

  /**
   * 一覧設定ダイアログ キャンセル @Click
   */
  const onClickListSettingCancel = (): void => {
    // 在庫移動・回収依頼一覧 ヘッダー設定用 クリア
    tableHeadersSetting.value = [];

    // 一覧設定ダイアログ非表示
    displayListSettingDialog.value = false;
  };

  /**
   * 一覧設定ダイアログ リセット @Click
   */
  const onClickListSettingReset = (): void => {
    // 在庫移動・回収依頼一覧 ヘッダー設定用 デフォルト値を設定
    tableHeadersSetting.value = JSON.parse(
      JSON.stringify(defaultTableHeaders)
    ) as TableHeader[];
  };

  /**
   * 一覧設定ダイアログ 設定 @Click
   */
  const onClickListSettingOk = (): void => {
    // 在庫移動・回収依頼一覧 ヘッダー へ設定を反映
    tableHeaders.value = tableHeadersSetting.value;

    // localStorage へ保存
    localStorageSetPageHeaders(
      DELIVERY_MOVE_LIST_FILE_NAME,
      JSON.stringify(tableHeadersSetting.value)
    );

    // 在庫移動・回収依頼一覧 ヘッダー設定用 クリア
    tableHeadersSetting.value = [];

    // 一覧設定ダイアログ非表示
    displayListSettingDialog.value = false;
  };

  return {
    defaultTableHeaders,
    tableHeaders,
    initTableHeaders,
    filteredTableHeaders,
    deliveryStatusName,
    pickupDeliveryStatusName,
    deliveryTypeItems,
    pickupDeliveryTypeName,
    listLoading,
    listOptions,
    listDataArray,
    listDataTotalCount,
    pageCount,
    selectListCheckbox,
    filteredSelectListCheckbox,
    isSearchButtonClick,
    displayPagingRange,
    tableHeadersSetting,
    displayListSettingDialog,
    onClickListSetting,
    onClickListSettingCancel,
    onClickListSettingReset,
    onClickListSettingOk,
  };
};
